import {point2d, point3d} from "@tarikjabiri/dxf";
import * as THREE from "three";

const FEET_PER_METER = 1 / 0.3048;
const INCHES_PER_METER = 1 / 0.0254;

/**
 * Converts a 3D point's x, y coordinates from meters to 2D feet-inches and returns it as a DXF 2D point.
 */
export function projectTo2DFeetInches(point){
   let x = point.x * INCHES_PER_METER;
   let y = point.y * INCHES_PER_METER;
   return { point: point2d(x, y) };
}

/**
 * Converts a raw distance (in feet) to a string format in feet and inches.
 */
export function inch2feet(value) {
   const distanceInMeters = Math.abs(Number(value) || 0);
   const totalFeet = distanceInMeters * FEET_PER_METER;
   const feet = Math.floor(totalFeet);
   const inches = Math.round((totalFeet - feet) * 12);
   const displayFeet = inches === 12 ? feet + 1 : feet;
   const displayInches = inches === 12 ? 0 : inches;
   let result = displayFeet + "'";
   if (displayInches > 0) {
      result += displayInches + '"';
   }
   return result;
}

/**
 * Finds the smallest x and y coordinate values in an array of 2D point objects.
 */
export function findSmallestVertex(polygons) {
   let smallest_x = Number.MAX_SAFE_INTEGER;
   let smallest_y = Number.MAX_SAFE_INTEGER;
   polygons.forEach(polygon => {
      smallest_x = Math.min(smallest_x, polygon.x);
      smallest_y = Math.min(smallest_y, polygon.y);
   });
   return [smallest_x.toFixed(2), smallest_y.toFixed(2)];
}

/**
 * Finds the largest x and y coordinate values in an array of 2D point objects.
 */
export function findLargestVertex(polygons) {
   let largest_x = Number.MIN_SAFE_INTEGER;
   let largest_y = Number.MIN_SAFE_INTEGER;
   polygons.forEach(polygon => {
      largest_x = Math.max(largest_x, polygon.x);
      largest_y = Math.max(largest_y, polygon.y);
   });
   return [largest_x.toFixed(2), largest_y.toFixed(2)];
}

/**
 * Calculates the length and width of a shape defined by a set of 2D points.
 */
export function calculateLengthWidth(data) {
   const smallestVertex = findSmallestVertex(data);
   const largestVertex = findLargestVertex(data);

   // Subtract smallest from largest to get dimensions
   const length = (largestVertex[0] - smallestVertex[0]).toFixed(2);
   const width = (largestVertex[1] - smallestVertex[1]).toFixed(2);

   let w = inch2feet(length);
   let l = inch2feet(width);

   return { l, w };
}

/**
 * Calculates the bounding box dimensions for all entities inside a DXF block.
 */
export function calculateBlockDimensions(block) {
   let minX = Number.MAX_SAFE_INTEGER;
   let maxX = Number.MIN_SAFE_INTEGER;
   let minY = Number.MAX_SAFE_INTEGER;
   let maxY = Number.MIN_SAFE_INTEGER;

   if (block.entities && Array.isArray(block.entities)) {
      block.entities.forEach(entity => {
         if (entity.vertices && Array.isArray(entity.vertices)) {
            entity.vertices.forEach(vertex => {
               minX = Math.min(minX, vertex.point.x);
               maxX = Math.max(maxX, vertex.point.x);
               minY = Math.min(minY, vertex.point.y);
               maxY = Math.max(maxY, vertex.point.y);
            });
         }
      });
   }

   const width = maxX - minX;
   const height = maxY - minY;
   return { minX, maxX, minY, maxY, width, height };
}

/**
 * Determines the bounding box dimensions for a polygon (in 2D feet-inches space).
 */
export function getPolygonDimensions(polygonVertices) {
   let minX = polygonVertices[0].point.x;
   let minY = polygonVertices[0].point.y;
   let maxX = polygonVertices[0].point.x;
   let maxY = polygonVertices[0].point.y;

   polygonVertices.forEach(point => {
      minX = Math.min(minX, point.point.x);
      minY = Math.min(minY, point.point.y);
      maxX = Math.max(maxX, point.point.x);
      maxY = Math.max(maxY, point.point.y);
   });

   const width = maxX - minX;
   const height = maxY - minY;

   return { width, height, minY, maxY, maxX, minX };
}

/**
 * Calculates the midpoint between two 2D points.
 */
export function calculateMidpoint(point1, point2) {
   return point3d(
      (point1.x + point2.x) / 2,
      (point1.y + point2.y) / 2
   );
}

/**
 * Returns the Euclidean distance between two 2D points.
 */
export const calculateDistance = (point1, point2) => {
   let dx = point2.x - point1.x;
   let dy = point2.y - point1.y;
   return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Computes the geometric center (centroid) of a polygon made of 2D points.
 */
export function calculateCenter(polygon3D) {
   const center = new THREE.Vector2();
   polygon3D.forEach((point) => {
      center.add(point.point); // Add vector coordinates
   });
   return point3d(center.x / polygon3D.length, center.y / polygon3D.length, 0);
}
